import { useMemo } from 'react';
import type { ChartData, ChartOptions, ChartType, Plugin } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Doughnut } from 'react-chartjs-2';
import type { DoughnutChartConfig } from '@/components/charts/chartConfig';
import { configuredOr } from '@/lib/configValue';
import '@/components/charts/register';

/**
 * The doughnut/ring chart, shared by `DoughnutWidget`. Port of `components/charts/DoughnutChart.vue`.
 *
 * The legacy binding was imperative — `new Chart(canvas, options)` in `onMounted`, the canvas
 * found with `document.getElementById` against a `Math.random()` id, and a manual
 * `chart.update()` inside a `watch`. `react-chartjs-2` owns the canvas ref and the update
 * cycle, so all of that is gone. What is *not* gone is the thing it was doing: the chart has
 * to redraw when a new MQTT payload arrives, and a chart that draws its first payload and
 * never redraws looks perfect in a screenshot. `DoughnutChart.test.tsx` asserts the second and
 * third payload reach the chart.
 */

export type DoughnutChartProps = {
  /** Segment values. `[0, 1]` before the first message — the widget supplies that default. */
  values: number[];
  /** Painted in the middle of the ring, when `chartConfig.chartText` is configured. */
  chartMiddleText?: string;
  config: DoughnutChartConfig;
  className?: string;
};

declare module 'chart.js' {
  /**
   * The middle-text plugin reads everything it paints from here rather than from a closure.
   *
   * That is not tidiness: `react-chartjs-2` passes the `plugins` array to the `Chart`
   * constructor and **never looks at it again**, so a plugin closing over `chartMiddleText`
   * would keep painting the value it was built with. Options, by contrast, are assigned onto
   * the live chart on every prop change, which makes this the one channel that stays current.
   */
  // The type parameter is fixed by the interface being augmented and is unused in this member;
  // module augmentation also requires `interface` rather than `type`.
  /* eslint-disable-next-line @typescript-eslint/no-unused-vars,
     @typescript-eslint/consistent-type-definitions */
  interface PluginOptionsByType<TType extends ChartType> {
    signageMiddleText: MiddleTextOptions;
  }
}

type MiddleTextOptions = {
  text: string;
  /** rem [sic] — see W-6 below. */
  fontSize?: number;
  fontFamily?: string;
  color?: string;
};

/**
 * `chartConfig.chartText` -> the label in the hole of the ring. Ported verbatim.
 *
 * **W-6, preserved.** `ctx.font` is assigned `'3rem Arial'` and canvas does not accept `rem`,
 * so the assignment is silently rejected and the text is painted in whatever font Chart.js
 * last set — not at the configured 3rem. Converting the value to pixels would honour the
 * config and change what four deployed displays render, so the current output is reproduced
 * instead (docs/MIGRATION-WIDGETS.md §8, W-6). This is the decision SYM-506 signs off.
 *
 * The 35px lift off `chartArea.bottom` is likewise a hardcoded legacy constant, and the
 * unbalanced `ctx.save()` is legacy too — see the note at the top of `barChartUtils.ts`.
 */
const middleTextPlugin: Plugin<'doughnut', MiddleTextOptions> = {
  id: 'signageMiddleText',
  beforeDraw(chart, _args, options) {
    const {
      ctx,
      chartArea: { width, bottom },
    } = chart;
    ctx.save();
    ctx.font = String(options.fontSize) + 'rem ' + String(options.fontFamily);
    ctx.textAlign = 'center';
    ctx.fillStyle = options.color ?? '';
    ctx.fillText(options.text, width / 2, bottom - 35);
  },
};

export function DoughnutChart({ values, chartMiddleText, config, className }: DoughnutChartProps) {
  const data = useMemo<ChartData<'doughnut', number[]>>(
    () => ({
      labels: config.labels,
      datasets: [
        {
          data: values,
          backgroundColor: config.labelColors,
          // `??`, not truthiness: these three are the keys the legacy code tested with
          // `!== undefined`, so a configured `0` borderRadius means 0 — and all four deployed
          // configs set exactly that.
          borderColor: config.chartBorderColor ?? '#FFF',
          cutout: config.cutout ?? '50%',
          borderRadius: config.borderRadius ?? 30,
        },
      ],
    }),
    [values, config],
  );

  const options = useMemo<ChartOptions<'doughnut'>>(
    () => ({
      // Truthiness here, matching legacy — a configured `circumference: 0` means 360.
      rotation: configuredOr(config.rotation, 0),
      circumference: configuredOr(config.circumference, 360),
      responsive: true,
      plugins: {
        legend: {
          display: config.grafLegend,
          position: 'bottom',
          labels: legacyLegendLabels(config),
        },
        // Only ever reached when `chartText` is absent — see the exclusivity note below.
        datalabels: {
          color: config.chartLabelColor,
          font(context) {
            const width = context.chart.width || 320;
            const size = Math.round(width / 32);
            return { size, weight: 600 };
          },
          formatter(value: number) {
            if (value === 0) {
              return '';
            }
            return value;
          },
        },
        signageMiddleText: {
          text: chartMiddleText ?? '',
          fontSize: config.chartText?.fontSize,
          fontFamily: config.chartText?.fontFamily,
          color: config.chartText?.color,
        },
      },
    }),
    [config, chartMiddleText],
  );

  /**
   * **The exclusivity rule, preserved** (docs/MIGRATION-WIDGETS.md §6): the legacy component
   * registers the middle-text plugin *instead of* `ChartDataLabels`, so configuring
   * `chartConfig.chartText` silently turns the per-segment value labels off. All four deployed
   * configs set `chartText`, which means datalabels is off on every display today.
   *
   * `ChartDataLabels` is passed inline here rather than registered globally, also as legacy
   * did — see the note in `register.ts` for why that matters to the bar chart.
   */
  const plugins = useMemo(
    () => [config.chartText !== undefined ? middleTextPlugin : ChartDataLabels],
    [config.chartText],
  );

  const maxHeight = config.maxHeight !== undefined ? `${String(config.maxHeight)}rem` : '10rem';

  return (
    <Doughnut
      data={data}
      options={options}
      plugins={plugins}
      className={className}
      style={{ height: maxHeight, maxHeight }}
    />
  );
}

type DoughnutOptions = ChartOptions<'doughnut'>;
type LegendLabelOptions = NonNullable<NonNullable<DoughnutOptions['plugins']>['legend']>['labels'];

/**
 * The legend's `labels` block, carried over with both of its legacy mistakes intact.
 *
 * - `color` is handed the whole `labelColors` **array**, where Chart.js wants one colour. An
 *   array is not a valid `fillStyle`, so the assignment is rejected and the label text keeps
 *   the previous fill.
 * - `size` is not a legend option at all; the font size belongs under `labels.font.size`.
 *
 * Neither is reachable on any deployed display — all four configs set `grafLegend: false`, so
 * the legend is not drawn — which is exactly why they are preserved rather than corrected:
 * "fixing" them would change the rendering of a legend nobody has seen, in a direction nobody
 * has asked for. The cast is deliberate and says so: Chart.js's typings reject this object
 * correctly, and it is being passed anyway.
 */
function legacyLegendLabels(config: DoughnutChartConfig): LegendLabelOptions {
  return {
    color: config.labelColors,
    size: Math.min(12, Math.max(5, window.innerWidth / 42)),
  } as unknown as LegendLabelOptions;
}
